// AI provider settings — mirrors the FastAPI schemas in
// backend/app/api/v1/ai_settings.py + the manifest/estimate in
// backend/app/services/ai_settings.py. Keys are server-side; the user only
// chooses which provider routes each (app, category).

import { api } from "./client";

// ---- Manifest -------------------------------------------------------------
export interface ProviderModelPrice {
  model: string;
  input_per_1m: number;
  output_per_1m: number;
}
export interface ProviderInfo {
  key: string;
  display_name: string;
  notes: string;
  generate_suitable: boolean;
  /** Selectable only when the server has the provider's key configured. */
  available: boolean;
  models: { flagship: ProviderModelPrice; fast: ProviderModelPrice };
}
export interface CategoryMeta {
  key: string;
  title: string;
  description: string;
  examples: string;
}
export interface AppInfo {
  slug: string;
  title: string;
}
export interface ImageProviderInfo {
  key: string;
  display_name: string;
  available: boolean;
  default_model: string | null;
  price_note: string;
  /** False → photo-edit operations always fall back to OpenAI. */
  supports_edit: boolean;
}
export interface ImageNote {
  key: string;
  title: string;
  locked_provider: string;
  description: string;
  examples: string;
  /** New selectable surface; old fields above are kept for legacy clients. */
  selectable?: boolean;
  default_provider?: string;
  providers?: ImageProviderInfo[];
}
export interface VisionProviderInfo {
  key: string;
  display_name: string;
  available: boolean;
}
export interface VisionInfo {
  providers: VisionProviderInfo[];
  fallback: { provider: string; model: string };
}
/** One "Fine-tune by task" row. `kind` says which storage the row writes:
 *  "category" → one root `cat:<target>` node; "categories" → one node per
 *  target; "app" → the target path verbatim; "image"/"vision" → the reserved
 *  override keys. */
export interface UseCase {
  key: string;
  title: string;
  description: string;
  examples: string;
  kind: "category" | "categories" | "app" | "image" | "vision";
  targets: string[];
}
export interface ModelPolicyMeta {
  key: "recommended" | "flagship" | "fast";
  title: string;
  description: string;
}
/** Curated engine preset — the primary user-facing choice. Mirrors
 *  backend/app/core/engine_presets.py + services/ai_settings._build_presets. */
export interface PresetInfo {
  key: string;
  display_name: string;
  description: string;
  /** False if a provider it points at has no server key (still works via fallback). */
  available: boolean;
  est_usd_per_run: number;
  est_tokens_per_run: number;
}
export interface AISettingsManifest {
  presets: PresetInfo[];
  providers: ProviderInfo[];
  categories: CategoryMeta[];
  use_cases: UseCase[];
  model_policies: ModelPolicyMeta[];
  apps: AppInfo[];
  image: ImageNote;
  vision: VisionInfo;
  server_default_provider: string;
}

// ---- Estimate -------------------------------------------------------------
export interface CategoryEstimate {
  category: string;
  provider: string;
  model: string;
  avg_tokens_per_run: number;
  est_usd_per_run: number;
}
export interface AppEstimate {
  slug: string;
  title: string;
  runs: number;
  has_history: boolean;
  avg_tokens_per_run: number;
  est_usd_per_run: number;
  categories: CategoryEstimate[];
}
export interface AccountEstimate {
  blended_usd_per_token: number;
  blended_usd_per_1m_tokens: number;
  total_runs: number;
  example: {
    framework_slug: string;
    title: string;
    avg_tokens_per_run: number;
    est_usd_per_run: number;
  } | null;
}
export interface Estimate {
  per_app: AppEstimate[];
  account: AccountEstimate;
}

// ---- Saved settings -------------------------------------------------------
export interface OverrideNode {
  provider: string;
  model?: string | null;
}
export interface AISettings {
  engine_preset: string | null;
  account_default_provider: string | null;
  /** Model policy paired with the provider: "flagship" | "fast" | null
   *  (null = "Recommended": best model for planning/generation, fast for the rest). */
  account_default_model: string | null;
  overrides: Record<string, OverrideNode>;
  updated_at: string | null;
  estimate: Estimate;
}
// PATCH-like: send only the fields you want to change; the backend keys off
// which fields are present (see put_settings). The provider-first UI always
// sends overrides_version: 2 (it understands the reserved cat:*/image/vision
// paths, so it gets replace semantics) and engine_preset: null (any custom
// choice replaces the legacy preset).
export interface AISettingsUpdate {
  engine_preset?: string | null;
  account_default_provider?: string | null;
  account_default_model?: string | null;
  overrides?: Record<string, OverrideNode>;
  overrides_version?: number;
}

export const getManifest = () => api.get<AISettingsManifest>("/ai-settings/manifest");
export const getAISettings = () => api.get<AISettings>("/ai-settings");
export const putAISettings = (body: AISettingsUpdate) =>
  api.put<AISettings>("/ai-settings", body);

// Node-path helpers (must match backend/app/core/llm_routing.py).
export const appPath = (slug: string) => `app:${slug}`;
export const appCatPath = (slug: string, category: string) => `app:${slug}/cat:${category}`;
